import {
  JOURNEY_TYPE,
  RAIL_EUROPE_TRAIN_CATEGORY_TYPE,
  TRAIN_PLACE_TYPE,
  TRAIN_PLACE_SPLIT_SYMBOL,
} from '~constants/pointToPointTicket';

import type {
  OptionalRailEuropeTrainQueryParam,
  RailEuropeTrainCategoryType,
} from '~types/queryParam';

export interface ParsedQueryResult {
  category: RailEuropeTrainCategoryType;
  isSingleTrip: boolean;
  origin: {
    place: string;
    type: (typeof TRAIN_PLACE_TYPE)[keyof typeof TRAIN_PLACE_TYPE];
    code: string;
  };
  destination: {
    place: string;
    type: (typeof TRAIN_PLACE_TYPE)[keyof typeof TRAIN_PLACE_TYPE];
    code: string;
  };
  departureDate?: string;
  arrivalDate?: string;
  travelersCount: number;
  ageList: number[];
  outboundDepartureTime?: string;
  inboundDepartureTime?: string;
}

/**
 * 레일유럽 검색 쿼리 파라미터를 파싱하는 유틸리티 함수
 */
// URL 쿼리 파라미터가 중복되면 router.query 값이 배열(string[])이 되어
// 이후 .split()/.replace()/date-fns parse()에서 크래시한다. 단일 문자열로 정규화한다.
const toStr = (v?: string | string[]): string | undefined =>
  v === undefined ? undefined : Array.isArray(v) ? v[0] : v;

const parseRailEuropeQueryParams = (
  queries: OptionalRailEuropeTrainQueryParam,
): ParsedQueryResult => {
  const {
    category: categoryQuery,
    journey: journeyQuery,
    origin: originQuery,
    destination: destinationQuery,
    departureDate: departureDateQuery,
    arrivalDate: arrivalDateQuery,
    ages: agesQuery,
    outboundDepartureTime: outboundDepartureTimeQuery,
    inboundDepartureTime: inboundDepartureTimeQuery,
  } = queries;

  const category = toStr(categoryQuery) as RailEuropeTrainCategoryType | undefined;
  const journey = toStr(journeyQuery);
  const departureDate = toStr(departureDateQuery);
  const arrivalDate = toStr(arrivalDateQuery);
  const ages = toStr(agesQuery);

  const [originCity, originType, originCode] =
    toStr(originQuery)?.split(TRAIN_PLACE_SPLIT_SYMBOL) || [];
  const originObj = {
    place: originCity ? decodeURIComponent(originCity) : '',
    type: originType as (typeof TRAIN_PLACE_TYPE)[keyof typeof TRAIN_PLACE_TYPE],
    code: originCode,
  };

  const [destinationCity, destinationType, destinationCode] =
    toStr(destinationQuery)?.split(TRAIN_PLACE_SPLIT_SYMBOL) || [];
  const destinationObj = {
    place: destinationCity ? decodeURIComponent(destinationCity) : '',
    type: destinationType as (typeof TRAIN_PLACE_TYPE)[keyof typeof TRAIN_PLACE_TYPE],
    code: destinationCode,
  };

  const travelersCount = ages ? decodeURIComponent(ages).split(',').length : 0;
  const ageList = ages
    ? decodeURIComponent(ages)
        .split(',')
        .map((age) => parseInt(age))
    : [];

  const isSingleTrip = journey ? journey === JOURNEY_TYPE.ONE_WAY : true;

  const outboundDepartureTimeStr = toStr(outboundDepartureTimeQuery);
  const outboundDepartureTime = outboundDepartureTimeStr
    ? decodeURIComponent(outboundDepartureTimeStr.replace(/:00:00/, ':00'))
    : undefined;

  const inboundDepartureTimeStr = toStr(inboundDepartureTimeQuery);
  const inboundDepartureTime = inboundDepartureTimeStr
    ? decodeURIComponent(inboundDepartureTimeStr.replace(/:00:00/, ':00'))
    : undefined;

  return {
    category: category || RAIL_EUROPE_TRAIN_CATEGORY_TYPE.TICKET,
    isSingleTrip,
    origin: originObj,
    destination: destinationObj,
    departureDate,
    arrivalDate,
    outboundDepartureTime,
    inboundDepartureTime,
    travelersCount,
    ageList,
  };
};

export default parseRailEuropeQueryParams;
